'use client'

/**
 * HOME INFLUENCER_SECTION PL widget (Quince IRL, `version: 2`) — widget-of-widget container.
 *
 * v2 nested pattern (see PRODUCT_OVERVIEW / the PDP IRL pair): the page renderer pre-renders the
 * nested INFLUENCER_CARD children and hands them in via `renderedChildren`; this container decides
 * how to arrange them. Like the non-PL `InfluencerV2` router it branches on
 * `shouldUseDesignSystem()`: the DS chrome reuses `influencer-v2-ds`'s exported classes/Carousel
 * arrows, the legacy chrome mirrors `InfluencerV2Legacy` byte-for-byte (same scss module, Swiper
 * -with-arrows (desktop, >4 cards) / SnapScroll split, dynamic style classes). List-level concerns
 * are shared by both branches — heading rich text, lazy loader, visibility + carousel telemetry,
 * single batched product fetch and the shoppable popup — and the per-card slice of that state
 * (play/mute, strip index, click) reaches each child via `InfluencerV2CardContext`.
 *
 * NOTE: this pair is home-specific. The PDP registry maps INFLUENCER_SECTION / INFLUENCER_CARD to
 * different widgets (`widgets/influencer-section`, `widgets/influencer-card`) that render the
 * non-DS `InfluencerSection` atom — do not swap them.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { Carousel, CarouselContent, CarouselItem } from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { SwiperRef } from 'swiper/react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { CustomSectionLazyLoader } from '@src/pages/[...slug]/components/collection/molecules/custom-section-lazy-loader/custom-section-lazy-loader.component'
import { Icon, LoaderWrapper, RichText } from '@src/quince-components/src'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { useInfluencerCardProducts } from '@src/quince-components/src/hooks/useInfluencerCardProducts'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { SnapScroll } from '@src/quince-components/src/molecules/snap-scroll'
import type { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import type InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import socialSectionStateManager from '@src/quince-site-common/src/redux/globalStateManager/SocialSectionStateManager'
import getContentContainerDetails from '@src/quince-site-common/src/utils/getContentContainerDetails'
import LeftArrowIcon from '@src/quince-site-integrated/src/assets/left-carousel-arrow.svg'
import RightArrowIcon from '@src/quince-site-integrated/src/assets/right-carousel-arrow.svg'
import {
  CarouselArrows,
  carouselContentCss,
  carouselRootCss,
  carouselWrapperClass,
  containerClass,
  contentClass,
  DESKTOP_VISIBLE_SLIDES,
  DS_CARD_HEIGHT_DESKTOP,
  DS_CARD_HEIGHT_MOBILE,
  slideItemCss,
  titleTextClass,
  titleWrapperClass,
} from '@src/SectionRenderer/sections/influencerV2/influencer-v2-ds'
import {
  getNavEdgeState,
  type SwiperEdgeState,
} from '@src/SectionRenderer/sections/influencerV2/influencer-v2-legacy.helpers'
import styles from '@src/SectionRenderer/sections/influencerV2/influencerV2.module.scss'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { toRichText } from '../widget-utils'

import { InfluencerV2CardContextProvider } from './influencer-v2-card-context'
import { adaptInfluencerV2Card } from './influencer-v2.adapter'

import type {
  InfluencerCardData,
  InfluencerSectionWidgetData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { Swiper as SwiperInstance } from 'swiper'

// @i18n-ignore
const NAVIGATION = 'navigation'
const ACTION_LEFT = 'click__carousel__left'
const ACTION_RIGHT = 'click__carousel__right'

enum ARROW_TYPES {
  LEFT,
  RIGHT,
}

const INFLUENCER_V2_ROOT_MARGIN = '100px'
// @i18n-ignore
const TRACKER = 'influencer_section'

interface NavState {
  isBeginning: boolean | undefined
  isEnd: boolean | undefined
}

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested INFLUENCER_CARD data via a narrow cast (same pattern as the PDP IRL widget).
interface InfluencerNestedWidget {
  data?: { influencerCard?: InfluencerCardData }
}

// Proto section data carries no analytics block, matching the legacy section's `trackingMetaData
// = {}` default (module constant so hook deps stay referentially stable).
const EMPTY_TRACKING_META_DATA: TrackingMetaData = {}

// PL-only DS layout shim: the widget shell (`<div data-widget-type>`) sits between the
// CarouselItem and the DS card's height:full container — stretch it so the card resolves against
// the square slide, matching the non-PL DOM where the card is the item's direct child.
const dsShellStretchCss = css.raw({
  '& > *': { width: 'full', height: 'full' },
})

// PL-only legacy layout shim — same shell problem as `dsShellStretchCss`, applied to the Swiper /
// SnapScroll slide: without it the shell collapses to the media's natural height, so the legacy
// card's height:100% container (which anchors the shop CTA + video controls to its bottom edge)
// tracks the media instead of the fixed square slide and the controls drift per card. Stretching
// the shell restores the non-PL geometry, and centering the card's in-flow child (the media —
// the CTA/controls/handle are absolutely positioned, so out of flow) keeps media whose natural
// height falls short of the square card vertically centered rather than top-pinned.
// Literal '100%' on purpose, NOT the `full` token: token utilities emit var(--web-ui-sizes-*),
// and panda.config's cssVarRoot scopes those vars to the DS html attributes — on a legacy page
// they are undefined and the declaration is silently dropped.
const legacyShellStretchClass = css({
  '& > *': { width: '100%', height: '100%' },
  '& > * > *': { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
})

function InfluencerV2WidgetComponent(props: PLTypedWidgetProps<InfluencerSectionWidgetData>) {
  const data = props.data.influencerSectionData
  const { t } = useTranslation('common')

  const [navState, setNavState] = useState<NavState>({ isBeginning: true, isEnd: undefined })
  const [currentPlayingVideo, setCurrentPlayingVideo] = useState<number | null>(null)
  const [isMuted, setIsMuted] = useState<boolean>(true)

  // Lazy-loader slug: the section data carries no slug, so use the page pathname — read from the
  // PL page meta (SSR-safe, same value on server and client) instead of window.location at render.
  const { pathname: pageSlug } = usePLPageMeta()
  const swiperRef = useRef<SwiperRef | null>(null)

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const trackingMetaData = EMPTY_TRACKING_META_DATA

  const nestedWidgets = (props as unknown as { widgets?: InfluencerNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren ?? []

  // Card details aligned 1:1 with nestedWidgets (and therefore with renderedChildren), preserving
  // index so a card's pre-rendered node can be paired with its data; non-card entries map to null.
  const allDetails = useMemo<(InfluencerDetails | null)[]>(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : []).map(widget => {
        const card = widget.data?.influencerCard
        return card ? adaptInfluencerV2Card(card) : null
      }),
    [nestedWidgets],
  )

  // Shoppable strip: same filter as the legacy section (`influencers.filter(d => d.spid)`).
  const filteredInfluencerDetailList = useMemo(
    () => allDetails.filter((detail): detail is InfluencerDetails => Boolean(detail?.spid)),
    [allDetails],
  )

  const showArrows = isDesktop && filteredInfluencerDetailList.length > 4
  // values taken from the css file, using showArrows as dependency since component being rendered is depending on it
  const minHeightForLazyLoadContainer = showArrows ? styles.cardHeightDesktop : styles.cardHeightMobile

  const { styleObject, className } = useDynamicStyle({
    dynamicValueClassesMobileOverride: data?.mobileDynamicClassesOverrides,
    baseDynamicValueClasses: data?.baseDynamicClasses,
    mobileClassNamesOverride: data?.classNamesMobileOverrides,
    baseClassNames: data?.classNames,
    postFix: styles.dynamicClassesPostFix ?? '',
    wrapperClassName: styles.dynamic_class_wrapper,
  })

  const getIsVisibleTrackParams = useCallback(
    () => ({
      ...trackingMetaData,
    }),
    [trackingMetaData],
  )

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const [intersectRef, isIntersecting] = useIntersect(null, '0px', 0, false)

  const playVideo = useCallback((videoToPlayID: number) => {
    setCurrentPlayingVideo(videoToPlayID)
  }, [])

  const stopVideo = useCallback(() => {
    setCurrentPlayingVideo(null)
  }, [])

  const toggleMute = useCallback(() => {
    setIsMuted(_isMuted => !_isMuted)
  }, [])

  const { isLoadingProducts, products, spidToProductMap } = useInfluencerCardProducts(filteredInfluencerDetailList)

  const handleCardClick = useCallback(
    (influencerIndex: number) => {
      const influencerDetail = filteredInfluencerDetailList[influencerIndex]
      const spid = influencerDetail?.spid
      const product = spid && spidToProductMap[spid]

      if (product) {
        const contentContainerDetails = getContentContainerDetails(trackingMetaData)
        const productTags = product.productTags.map(tag => {
          return tag.value as ProductTagKey
        })

        trackClickEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
          context: {
            ...trackingMetaData.context,
            contentContainerDisplayName: contentContainerDetails.contentContainerDisplayName,
            contentContainerType: contentContainerDetails.contentContainerType,
            contentContainerName: contentContainerDetails.contentContainerName,
            productPosition: influencerIndex,
            tags: productTags,
            tag_ids: transformTagsIntoAnalyticsTags(productTags),
            ...getIndividualTagStatus(productTags),
          },
        })
      }

      socialSectionStateManager.showSocialSectionPopUp({
        influencerDetails: filteredInfluencerDetailList,
        influencerIndex: influencerIndex,
        isLoadingProducts,
        products,
        // Shared INFLUENCER_V2 IRL carousels pass false and keep the default modal nav arrows
        // (true is reserved for the EVENT_HIGHLIGHTS section type).
        isHighlights: false,
      })
    },
    [filteredInfluencerDetailList, isLoadingProducts, products, spidToProductMap, trackingMetaData],
  )

  useEffect(() => {
    if (isIntersecting) {
      setCurrentPlayingVideo(0)
    }
  }, [isIntersecting])

  // Sync arrow visibility from swiper's live position. Driven by the Swiper
  // atom's afterChange (fires on slide change / scroll / scrollbar drag) so BOTH
  // the freeMode scroll path and arrow clicks keep the nav arrows honest. The
  // edge logic (incl. the grouped-snap sliver) lives in getNavEdgeState. The live
  // instance structurally satisfies SwiperEdgeState; the cast bridges the repo's
  // mismatched swiper type declarations (same mismatch the legacy section trips).
  const syncNavState = useCallback((swiper: SwiperInstance) => {
    setNavState(getNavEdgeState(swiper as unknown as SwiperEdgeState))
  }, [])

  const updateNavState = () => {
    const swiper = swiperRef.current?.swiper
    if (swiper) syncNavState(swiper)
  }
  const fireArrowClickEvent = (arrowType: ARROW_TYPES) => {
    trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      trackGA: true,
      trackInternal: true,
      category: NAVIGATION,
      action: arrowType === ARROW_TYPES.LEFT ? ACTION_LEFT : ACTION_RIGHT,
      context: trackingMetaData.context,
    })
  }
  const onArrowActivate = (arrowType: ARROW_TYPES) => {
    const swiper = swiperRef.current?.swiper
    if (arrowType === ARROW_TYPES.LEFT) swiper?.slidePrev()
    else swiper?.slideNext()
    updateNavState()
    fireArrowClickEvent(arrowType)
  }
  // Keyboard parity for the div-based nav arrows (role="button"): activate on Enter/Space like a
  // native button, preventing default so Space doesn't scroll the page.
  const onArrowKeyDown = (arrowType: ARROW_TYPES) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onArrowActivate(arrowType)
  }
  const renderLeftArrowIcon = () => {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={t('previous')}
        className={classnames(styles.arrowIcon, styles.arrowIcon__left, {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          [styles.hideNavIcon!]: navState.isBeginning,
        })}
        onClick={() => {
          onArrowActivate(ARROW_TYPES.LEFT)
        }}
        onKeyDown={onArrowKeyDown(ARROW_TYPES.LEFT)}
      >
        <Icon iconSvg={LeftArrowIcon} />
      </div>
    )
  }
  const renderRightArrowIcon = () => {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={t('next')}
        className={classnames(styles.arrowIcon, styles.arrowIcon__right, {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          [styles.hideNavIcon!]: navState.isEnd,
        })}
        onClick={() => {
          onArrowActivate(ARROW_TYPES.RIGHT)
        }}
        onKeyDown={onArrowKeyDown(ARROW_TYPES.RIGHT)}
      >
        <Icon iconSvg={RightArrowIcon} />
      </div>
    )
  }
  const renderArrowIcons = () => {
    return (
      <>
        {renderLeftArrowIcon()}
        {renderRightArrowIcon()}
      </>
    )
  }

  if (!data) return null

  // Every shoppable card's pre-rendered node becomes a slide, wrapped in a context provider whose
  // value is bound to that card's index within filteredInfluencerDetailList — so play state and the
  // popup position match the non-PL sections' `filteredInfluencerDetailList.map(...)` exactly.
  // `stripIndex` advances for every shoppable card (matching the filter above).
  let stripIndex = -1
  const slideEntries: { key: string; node: JSX.Element }[] = []
  allDetails.forEach((detail, index) => {
    if (!detail?.spid) return
    stripIndex += 1
    const node = renderedChildren[index]?.node
    if (!node) return
    const cardIndex = stripIndex
    slideEntries.push({
      key: detail.id || String(index),
      node: (
        <InfluencerV2CardContextProvider
          key={detail.id || index}
          value={{
            index: cardIndex,
            isPlaying: currentPlayingVideo === cardIndex,
            isMuted,
            playVideo,
            stopVideo,
            muteVideo: toggleMute,
            trackingMetaData,
            tracker: TRACKER,
            onCardClick: handleCardClick,
          }}
        >
          {node}
        </InfluencerV2CardContextProvider>
      ),
    })
  })

  if (!slideEntries.length) return null

  const slides = slideEntries.map(entry => entry.node)
  const headingRichText = toRichText(data.heading as Parameters<typeof toRichText>[0])

  // DS branch — mirrors `InfluencerV2DS` (same exported classes, Carousel opts, arrows, lazy
  // loader), arranging pre-rendered children instead of mapping data to cards. DS ignores the
  // Contentful dynamic classes, exactly like the non-PL DS section.
  if (shouldUseDesignSystem()) {
    const dsMinHeight = showArrows ? DS_CARD_HEIGHT_DESKTOP : DS_CARD_HEIGHT_MOBILE
    return (
      <div className={containerClass} ref={trackIsVisibleRef}>
        <div className={contentClass}>
          <div className={titleWrapperClass}>
            <RichText {...RichTextDomain.makeRichTextProps(headingRichText)} className={titleTextClass} />
          </div>
          <div ref={intersectRef} className={carouselWrapperClass}>
            <CustomSectionLazyLoader
              slug={pageSlug}
              isPageCache={false}
              fallback={
                !isDesktop ? (
                  <LoaderWrapper showLoader={true}>
                    <div />
                  </LoaderWrapper>
                ) : null
              }
              minHeight={dsMinHeight}
              rootMargin={INFLUENCER_V2_ROOT_MARGIN}
            >
              <Carousel
                opts={{
                  align: 'start',
                  containScroll: 'trimSnaps',
                  dragFree: false,
                  slidesToScroll: 1,
                  breakpoints: {
                    '(min-width: 1200px)': {
                      slidesToScroll: DESKTOP_VISIBLE_SLIDES,
                    },
                  },
                }}
                css={carouselRootCss}
              >
                <CarouselContent css={carouselContentCss}>
                  {slideEntries.map(entry => (
                    <CarouselItem key={entry.key} css={{ ...slideItemCss, ...dsShellStretchCss }}>
                      {entry.node}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselArrows trackingContext={trackingMetaData.context} />
              </Carousel>
            </CustomSectionLazyLoader>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classnames(styles.container, className)} style={styleObject} ref={trackIsVisibleRef}>
      <RichText {...RichTextDomain.makeRichTextProps(headingRichText)} />
      <div ref={intersectRef} className={styles.swiperContainer}>
        <CustomSectionLazyLoader
          slug={pageSlug}
          isPageCache={false}
          fallback={
            !isDesktop ? (
              <LoaderWrapper showLoader={true}>
                <div />
              </LoaderWrapper>
            ) : null
          }
          minHeight={minHeightForLazyLoadContainer}
          rootMargin={INFLUENCER_V2_ROOT_MARGIN}
        >
          {showArrows ? (
            <>
              <Swiper
                ref={swiperRef}
                className={styles.slidesWrapper}
                numberOfVisibleSlides={4}
                variableWidth
                showNavigator={false}
                slideClassName={classnames(styles.cardSlide, legacyShellStretchClass)}
                afterChange={syncNavState}
              >
                {slides}
              </Swiper>
              {renderArrowIcons()}
            </>
          ) : (
            <SnapScroll
              key={'influencer-swiper'}
              spaceBetween={4}
              scrollEventDebounceTime={250}
              slideClassName={classnames(styles.cardSlide, legacyShellStretchClass)}
              id={trackingMetaData.context?.id ?? 'influencer-snap-scroll'}
            >
              {slides}
            </SnapScroll>
          )}
        </CustomSectionLazyLoader>
      </div>
    </div>
  )
}

export const InfluencerV2Widget: PLTypedWidgetInterface<InfluencerSectionWidgetData, WidgetType.INFLUENCER_SECTION> = {
  id: WidgetType.INFLUENCER_SECTION,
  errorBoundary: PLBaseErrorBoundary,
  render: InfluencerV2WidgetComponent,
}
